import { useState } from "react";
import { Monitor, Smartphone } from "lucide-react";

/**
 * Live rendered email preview — mirrors the server's `email_shell()` HTML
 * closely enough that admins see exactly what lands in inboxes before
 * firing. Includes a Desktop / Mobile toggle so admins can eyeball how
 * the email looks in narrow inboxes (iPhone / Android Gmail app) too.
 */
export default function BroadcastEmailPreview({ form, segment, desireTag, segmentCount, sender }) {
  const [device, setDevice] = useState("desktop"); // "desktop" | "mobile"
  const isMobile = device === "mobile";

  const audienceLabel = () => {
    if (segment === "creators") return "creators";
    if (segment === "desire") return `people who share "${desireTag || "…"}"`;
    return "everyone on My Panda X";
  };

  return (
    <div className="hidden lg:block sticky top-4" data-testid="broadcast-email-preview">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center justify-between gap-2">
        <span>Email preview · what recipients see</span>
        <div className="flex items-center gap-1">
          {typeof segmentCount === "number" && segmentCount > 0 && (
            <span className="text-emerald-400 normal-case tracking-normal mr-2">
              → {segmentCount.toLocaleString()} {audienceLabel()}
            </span>
          )}
          <DeviceToggle device={device} onChange={setDevice} />
        </div>
      </div>
      <div
        className={`mx-auto rounded-lg border border-white/10 bg-zinc-950 overflow-hidden shadow-xl transition-all duration-300 ${
          isMobile ? "max-w-[340px]" : "max-w-full"
        }`}
        data-testid={`broadcast-email-preview-${device}`}
      >
        {/* Fake email header */}
        <div className="bg-black/60 border-b border-white/5 px-4 py-2.5 flex items-center gap-2">
          <div className="flex gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="ml-3 min-w-0 flex-1">
            <div className="text-[11px] text-zinc-500 truncate">
              From: <span className="text-zinc-300">{sender || "(sender not configured)"}</span>
            </div>
            <div className="text-[11px] text-zinc-500 truncate">
              Subject: <span className="text-white">{form.subject || "(no subject)"}</span>
            </div>
          </div>
        </div>

        {/* Body — dark rose-tinted template to mirror the server's email_shell */}
        <div
          className={isMobile ? "p-4 text-sm" : "p-5 text-sm"}
          style={{
            background: "radial-gradient(circle at 20% 0%, #3f0a1b 0%, #0a0a0a 60%)",
            color: "#e4e4e7",
            fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
            minHeight: "260px",
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: 4, textTransform: "uppercase", color: "#f43f5e", marginBottom: 12 }}>
            My Panda X
          </div>
          <h1
            style={{
              fontFamily: '"Playfair Display", Georgia, serif',
              fontSize: isMobile ? 22 : 26,
              lineHeight: 1.15,
              margin: "0 0 14px",
              color: "#fff",
            }}
          >
            {form.heading || "(heading)"}
          </h1>
          <div style={{ color: "#a1a1aa", fontSize: isMobile ? 13 : 14, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
            {form.body || "(body will render here)"}
          </div>
          {form.cta_url && form.cta_label && (
            <div style={{ margin: "20px 0 6px" }}>
              <span
                style={{
                  display: "inline-block",
                  background: "#e11d48",
                  color: "#fff",
                  padding: isMobile ? "10px 20px" : "12px 24px",
                  textDecoration: "none",
                  fontWeight: 600,
                  letterSpacing: 1,
                  fontSize: isMobile ? 12 : 13,
                }}
              >
                {form.cta_label}
              </span>
            </div>
          )}
          <div style={{ color: "#52525b", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginTop: 22 }}>
            You're receiving this because you signed up at mypandax.com.
          </div>
        </div>
      </div>
    </div>
  );
}

/** Desktop / Mobile segmented control for the preview. */
function DeviceToggle({ device, onChange }) {
  const opts = [
    { v: "desktop", Icon: Monitor, label: "Desktop" },
    { v: "mobile", Icon: Smartphone, label: "Mobile" },
  ];
  return (
    <div
      className="inline-flex items-center bg-black/40 border border-white/10 rounded-full p-0.5"
      data-testid="broadcast-preview-device-toggle"
    >
      {opts.map(({ v, Icon, label }) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          data-testid={`broadcast-preview-device-${v}`}
          title={label}
          aria-pressed={device === v}
          className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest transition ${
            device === v ? "bg-rose-600 text-white" : "text-zinc-500 hover:text-white"
          }`}
        >
          <Icon size={11} />
        </button>
      ))}
    </div>
  );
}
